/**
 * Web Crypto API-based encryption utility for frontend
 * Matches backend Node.js crypto implementation using AES-256-GCM
 */

// Get encryption key (raw bytes)
const getKeyBytes = () => {
  // const keyEnv = '-a154db46-95e3-4253-8de4-df2191856943';
  const keyEnv = process.env.GATSBY_WEB_ENCRYPTION_KEY || '-a154db46-95e3-4253-8de4-df2191856943';
  if (!keyEnv) {
    throw new Error('GATSBY_WEB_ENCRYPTION_KEY is not set in environment');
  }

  // Try to interpret as base64 first
  try {
    const keyData = Uint8Array.from(atob(keyEnv), c => c.charCodeAt(0));
    if (keyData.length === 32) {
      return keyData;
    }
  } catch (e) {
    // Not valid base64 or wrong length
  }

  // Try hex
  try {
    const hexMatch = keyEnv.match(/.{1,2}/g);
    if (hexMatch) {
      const keyData = new Uint8Array(hexMatch.map(byte => parseInt(byte, 16)));
      if (keyData.length === 32) {
        return keyData;
      }
    }
  } catch (e) {
    // Not valid hex or wrong length
  }

  // Fallback: use utf8 and pad/truncate to 32 bytes
  const encoder = new TextEncoder();
  const keyData = new Uint8Array(32);
  const encoded = encoder.encode(keyEnv);
  keyData.set(encoded.slice(0, 32));
  
  return keyData;
};

// Import key for Web Crypto API
const getEncryptionKey = async () => {
  const keyBytes = getKeyBytes();
  return await crypto.subtle.importKey(
    'raw',
    keyBytes,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt']
  );
};

/**
 * Convert ArrayBuffer to base64 string
 */
const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/**
 * Convert base64 string to ArrayBuffer
 */
const base64ToArrayBuffer = (base64) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

/**
 * Encrypt data using AES-256-GCM to match Node.js backend exactly
 * @param {string|object} plain - Data to encrypt (string or object that will be JSON.stringified)
 * @returns {Promise<string>} Encrypted string in format "iv:encrypted:tag" (all base64)
 */
export const encrypt = async (plain) => {
  try {
    const key = await getEncryptionKey();
    const data = typeof plain === 'string' ? plain : JSON.stringify(plain);
    
    // Generate random 12-byte IV (96-bit recommended for GCM)
    const iv = crypto.getRandomValues(new Uint8Array(12));
    
    // Encode the data
    const encoder = new TextEncoder();
    const encodedData = encoder.encode(data);
    
    // Encrypt with AES-GCM
    // The Web Crypto API returns ciphertext with auth tag appended
    const encryptedWithTag = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: 128 // 16 bytes auth tag
      },
      key,
      encodedData
    );
    
    // Web Crypto API appends the 16-byte auth tag to the end of ciphertext
    const encryptedArray = new Uint8Array(encryptedWithTag);
    const ciphertextLength = encryptedArray.length - 16;
    const ciphertext = encryptedArray.slice(0, ciphertextLength);
    const tag = encryptedArray.slice(ciphertextLength);
    
    // Return in format: iv:encrypted:tag (all base64)
    // This matches the backend format exactly
    return `${arrayBufferToBase64(iv)}:${arrayBufferToBase64(ciphertext)}:${arrayBufferToBase64(tag)}`;
  } catch (error) {
    console.error('Encryption error:', error);
    throw new Error('Failed to encrypt data');
  }
};

/**
 * Decrypt data using AES-256-GCM
 * @param {string} encryptedString - Encrypted string in format "iv:encrypted:tag"
 * @returns {Promise<string|object>} Decrypted data (parsed as JSON if possible)
 */
export const decrypt = async (encryptedString) => {
  try {
    if (!encryptedString || typeof encryptedString !== 'string') {
      throw new Error('Invalid encrypted payload');
    }
    
    const parts = encryptedString.split(':');
    if (parts.length !== 3) {
      throw new Error('Invalid encrypted payload format');
    }
    
    const key = await getEncryptionKey();
    const iv = new Uint8Array(base64ToArrayBuffer(parts[0]));
    const ciphertext = new Uint8Array(base64ToArrayBuffer(parts[1]));
    const tag = new Uint8Array(base64ToArrayBuffer(parts[2]));
    
    // Combine ciphertext and tag for Web Crypto API
    const encryptedData = new Uint8Array(ciphertext.length + tag.length);
    encryptedData.set(ciphertext);
    encryptedData.set(tag, ciphertext.length);
    
    // Decrypt
    const decrypted = await crypto.subtle.decrypt(
      {
        name: 'AES-GCM',
        iv: iv,
        tagLength: 128
      },
      key,
      encryptedData
    );
    
    const decoder = new TextDecoder();
    const decryptedString = decoder.decode(decrypted);
    
    // Try to parse as JSON
    try {
      return JSON.parse(decryptedString);
    } catch (e) {
      return decryptedString;
    }
  } catch (error) {
    console.error('Decryption error:', error);
    return { error: 'Decryption failed - possible tampering or invalid data' };
  }
};

/**
 * Encrypt payload for web login (wrapper for consistency with backend)
 * @param {object} payload - Login payload to encrypt
 * @returns {Promise<string>} Encrypted payload string
 */
export const encryptWebLoginParams = async (payload) => {
  return await encrypt(payload);
};
